/**
 * Searchable single-select dropdown.
 *
 * Backed by a popover (HTML <details>-style without the disclosure widget) +
 * a filtered <input>. Used by SkillForm for any `type: select` input that has
 * many options (objects, classes, fields, …) where Radix's plain Select would
 * force the user to scroll through 600+ rows.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { cn } from "@/lib/studio/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
  id?: string;
  metadata_id?: string;
  metadata_item_id?: string;
  metadata_type?: string;
  full_name?: string;
  name?: string;
  file_name?: string;
  api_name?: string;
  [key: string]: unknown;
}

export interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  emptyHint?: string;
  className?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  emptyHint = "No options match.",
  className,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Focus the search input when popover opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      // microtask delay so the input exists
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? "",
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.value.toLowerCase().includes(q) ||
        o.label.toLowerCase().includes(q)
    );
  }, [options, query]);

  const onSelect = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex min-h-9 w-full items-start justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono",
          "focus:outline-none focus:ring-1 focus:ring-ring",
          disabled && "cursor-not-allowed opacity-50"
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          title={selectedLabel || placeholder}
          className={cn(
            "min-w-0 flex-1 whitespace-normal break-words text-left leading-5",
            !selectedLabel && "text-muted-foreground"
          )}
        >
          {selectedLabel || placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1 pt-0.5">
          {value && !disabled ? (
            <X
              className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
            />
          ) : null}
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
        </span>
      </button>

      {open ? (
        <div className="absolute left-0 z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full bg-transparent text-sm font-mono outline-none placeholder:text-muted-foreground"
            />
            <span className="text-[10px] font-mono text-muted-foreground">
              {filtered.length}
              {filtered.length !== options.length ? ` / ${options.length}` : ""}
            </span>
          </div>
          <ul role="listbox" className="max-h-72 overflow-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs font-mono text-muted-foreground">
                {emptyHint}
              </li>
            ) : (
              filtered.map((opt) => {
                const selected = opt.value === value;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      onClick={() => onSelect(opt.value)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm font-mono",
                        "hover:bg-muted hover:text-foreground",
                        selected && "bg-muted text-foreground"
                      )}
                      role="option"
                      aria-selected={selected}
                      title={opt.label}
                    >
                      <span className="min-w-0 whitespace-normal break-words leading-5">
                        {opt.label}
                      </span>
                      {selected ? (
                        <Check className="h-4 w-4 shrink-0 text-foreground" />
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {hasMore ? (
            <div className="border-t border-border">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loadingMore}
                className={cn(
                  "flex w-full items-center justify-center gap-2 px-3 py-2 text-xs font-mono text-muted-foreground",
                  "hover:bg-muted hover:text-foreground",
                  loadingMore && "cursor-not-allowed opacity-50"
                )}
              >
                {loadingMore ? "Loading…" : "Load more…"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
